/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { mergeMap, map } from 'rxjs/operators';
import { from, Observable } from 'rxjs';

import { logAndCatchError } from '../utils/rxjs-utils';
import { resolveRestApi } from '../router/app-router';
import constants, { publicConstants } from '../constants';
import * as StateManager from '../store/state/state-manager';
import TargetMap from '../actions/targetmap';
import Utils from '../utils/utils';
import CaseUtils from '../case/utils';
import { getTranslatedDXAPIResponse } from '../utils/dxapi-response-translator';
import { getResolvedTargetName } from '../router/routing-manager';
import { ActionManagerInstance } from '../actions/manager';
import * as actionType from '../store/state/actions/index';
import * as actions from '../store/state/actions/action-creators';
import StateMachineManager from '../page-instructions/state-machine-manager';
import {
  processViewResponse,
  getActionPayload,
  processHeaders,
  processDataToDispatchErrorAction,
  getSubmitData,
  getParentContextChangeSet,
  mergeObjectsAndConcatArrays
} from '../store/state/epics/util';
import { fetch, getCancelTokenSource, getActiveRequestCountPerContext } from '../service_broker/fetch';
import * as RuleStore from '../rules/rules-store';
import type { Action, State } from '../store/state/state-manager';
import ActionsSequencer from '../actions/sequencer';
import type { ActionConfig, Payload } from '../contextree/types';
import EnvironmentInfo from '../environment-info';
import type { CalculatedField } from '../types';
import type { RestAPIPayload } from '../router/types';
import type { PropertyModel } from '../rules/types';
import ContextTreeManager from '../contextree/treemanager';
import PubSubUtils from '../utils/pubsub-utils';
import { getRefreshRouteKeyForEmbedded } from '../case/open-embedded-data-modal/async-request';

import {
  appendQueryParamsForRefreshAPI,
  getItemsToRecalculate,
  triggerTransformCaseToObject,
  shouldCancelRequest,
  isParentObjectType
} from './refreshHelpers';
import type { ActionPayload, RefreshPayLoad, ReqConfig, RequestBody } from './types';

import refreshConstants from './index';

const reqConfigList: { [key: string]: ReqConfig[] } = {};
const { VIEW, FIELD } = constants.CONTEXT_TREE_NODE_TYPES;

const prepareRequestContext = (payload: RefreshPayLoad) => {
  const { ID, actionID, context } = payload;
  return `${ID}.${context}.${actionID}`;
};

const getRequestHeaders = (isObjectType: boolean | undefined, state$: { value: State }, context: string) => {
  const objectInfoHeaders = state$.value.data[context]?.objectInfo?.headers;
  const caseInfoHeaders = state$.value.data[context]?.caseInfo?.headers;
  return isObjectType && objectInfoHeaders ? objectInfoHeaders : caseInfoHeaders;
};

const prepareRequestHeaders = (reqConfig: ReqConfig) => {
  const { reqHeaders } = reqConfig;
  if (!reqHeaders) return;

  reqConfig.headers = reqConfig.headers || {};

  if (reqHeaders.etag) {
    reqConfig.headers['if-match'] = reqHeaders.etag;
  }
};

const hasActiveRequestsForContext = (reqContext: string) => {
  return getActiveRequestCountPerContext(reqContext) > 0 && reqConfigList[reqContext];
};

/**
 * This helper function refresh the case view, case data or assignments
 * Track the pending requests fired for a view and cancel them if any subsequent
 * requests fired again for the same view.
 * @param reqConfig contains info about the http request
 * @param payload contains view information
 */
const refreshCaseViewDataOrAssignment = (reqConfig: ReqConfig, payload: RefreshPayLoad) => {
  const { context } = reqConfig;
  prepareRequestHeaders(reqConfig);

  const reqContext = prepareRequestContext(payload);
  if (payload.interestPage) {
    reqConfig.interestPage = payload.interestPage;
  }
  if (hasActiveRequestsForContext(reqContext)) {
    const toBeDeletedRequests: number[] = [];
    for (let index = 0; index < reqConfigList[reqContext].length; ) {
      const request = reqConfigList[reqContext][index];
      const cancelDecision = shouldCancelRequest(request, reqConfig);

      if (cancelDecision === 'CANCEL_REFRESH') {
        const error = new Error() as unknown as { code: string };
        error.code = 'ERR_CANCELED';
        return Promise.reject(error);
      }

      if (cancelDecision === true) {
        toBeDeletedRequests.push(index);
        request.cancelConfig?.cancel();
      }
      index += 1;
    }
    toBeDeletedRequests.forEach((index: number) => {
      reqConfigList[reqContext].splice(index, 1);
    });
  }

  const cancelToken = getCancelTokenSource();
  reqConfig.signal = cancelToken.token;
  reqConfig.cancelConfig = cancelToken;
  if (reqConfigList[reqContext]) {
    reqConfigList[reqContext].push(reqConfig);
  } else {
    reqConfigList[reqContext] = [reqConfig];
  }
  PubSubUtils.publish(constants.ON_REFRESH_START, {
    context
  });
  return fetch(reqConfig.url, reqConfig, context, { cancelContext: reqContext })
    .then((response) => {
      delete reqConfigList[reqContext];
      return response;
    })
    .catch((error) => {
      if (error && error.code !== 'ERR_CANCELED') {
        delete reqConfigList[reqContext];
      }
      throw error;
    });
};

const handleViewsWithWhen = (payload: ActionPayload, caseWideAction: boolean) => {
  const { isObjectType, isExternalObject, actionID, context } = payload;
  // Return for case-wide action or object type
  // If parent is case type, we should continue with refresh assignment
  if (caseWideAction || (isObjectType && isParentObjectType(context))) {
    const refreshType =
      isExternalObject && actionID === 'Create'
        ? refreshConstants.REFRESH_EXTERNAL_OBJECT
        : refreshConstants.REFRESH_CASE_DATA;

    return resolveRestApi(refreshType, payload);
  }

  // Default case for assignment data
  return resolveRestApi(refreshConstants.REFRESH_ASSIGNMENT_DATA, payload);
};

const handleFieldsWithCalculations = (
  payload: ActionPayload,
  fieldsWithDeclarativeTargets: CalculatedField[],
  fieldsWithWhen: CalculatedField[],
  caseWideAction: boolean,
  requestBody: RequestBody
) => {
  requestBody = {
    ...requestBody,
    calculations: {
      fields: fieldsWithDeclarativeTargets,
      whens: fieldsWithWhen
    }
  };

  const restAPI = caseWideAction
    ? resolveRestApi(refreshConstants.RECALCULATE_CASE_DATA, payload)
    : resolveRestApi(refreshConstants.RECALCULATE_ASSIGNMENT_DATA, payload);

  const requestType = refreshConstants.RECALCULATE;

  return { restAPI, requestBody, requestType };
};

const getRestAPIAndBody = (payload: ActionPayload, body: RequestBody) => {
  const {
    caseWideAction,
    caseViewAction,
    fieldsWithDeclarativeTargets,
    fieldsWithWhen,
    isViewsWithWhen,
    interestPage,
    options,
    isMultiRecordData,
    editType,
    isLocalAction,
    interestPageActionID
  } = payload;

  const { optionalPayload } = options;

  // Set optional payload if available
  if (optionalPayload) {
    body.content = { ...optionalPayload };
  }

  let restAPI: RestAPIPayload = {};
  let requestBody = { ...body };
  let requestType = refreshConstants.REFRESH;

  // Determine restAPI and requestBody based on conditions
  if (caseViewAction || (isMultiRecordData && editType === constants.ACTION)) {
    const routeKey = getRefreshRouteKeyForEmbedded(editType, isLocalAction);
    if (isMultiRecordData && editType === constants.ACTION) {
      requestBody.interestPageActionID = interestPageActionID;
    }
    restAPI = resolveRestApi(routeKey, payload);
    requestBody = { ...requestBody, interestPage };
  } else if (isViewsWithWhen) {
    restAPI = handleViewsWithWhen(payload, caseWideAction);
  } else if (fieldsWithDeclarativeTargets.length > 0 || fieldsWithWhen.length > 0) {
    ({ restAPI, requestBody, requestType } = handleFieldsWithCalculations(
      payload,
      fieldsWithDeclarativeTargets,
      fieldsWithWhen,
      caseWideAction,
      requestBody
    ));
  }

  return { restAPI, requestBody, requestType };
};

const updateServerPrecedenceFields = (
  parentProperty: string,
  classID: string,
  changeSet: { [key: string]: any },
  leafPropertyName: string | undefined,
  serverPrecedenceFields: string[]
) => {
  const referencedMetaData = RuleStore.getPropertyMetadata(parentProperty, classID) as PropertyModel;
  if (!referencedMetaData || referencedMetaData.dataRetrievalType !== 'copy') {
    return;
  }
  const propertyObject = Utils.getIn(changeSet, `content.content.${parentProperty}`);
  if (!propertyObject || typeof propertyObject !== 'object') {
    return;
  }
  const keysToRemove = leafPropertyName
    ? Object.keys(propertyObject).filter((key) => key !== leafPropertyName)
    : Object.keys(propertyObject);
  if (keysToRemove.length > 0) {
    keysToRemove.forEach((key) => delete propertyObject[key]);
    serverPrecedenceFields.push(parentProperty);
  }
};

/**
 * [refreshData]
 * Description : Epic to do side effect - refreshing a view.
 * ACTION_IN -  REFRESH_DATA
 * ACTION_OUT - ERROR or MERGE_DATA
 * @param action$ (Observable<Action>)
 * @param state$ (Returns the object with headers)
 */
const refreshData = (action$: Observable<Action>, state$: { value: any }) => {
  return action$.pipe(
    ofType(refreshConstants.REFRESH_DATA, actionType.FILL_FORM_WITH_AI), // Check the action is "REFRESH_DATA" or "FILL_FORM_WITH_AI"
    map((action: Action) => {
      return action.payload as RefreshPayLoad;
    }), // Pass on the payload
    mergeMap((payload: RefreshPayLoad) => {
      const { context, contextData, options, isObjectType } = payload;
      const { propertyName = '', classID = '', isObjectWideAction } = options;
      const changeSet = getSubmitData(context, { skipUUID: isObjectType, isObjectWideAction });
      let fieldsWithDeclarativeTargets: CalculatedField[] = [];
      let whens: CalculatedField[] = [];
      let isViewsWithWhen = false;

      const featureMap: { granularUIRefresh: boolean } = EnvironmentInfo.getFeatureMap() as {
        granularUIRefresh: boolean;
      };
      if (!featureMap.granularUIRefresh) {
        /**
         * This check helps to fallback refersh implementation because either the DXAPI for recalculate is not done or
         * the portal is on Launchpad as recalculate feature is not supported in Launchpad.
         * The Logic:  `isViewsWithWhen` is true means that there are `WHEN` condition configured on views in this
         * scenario we are going with the default implementation of calculating /refresh endpoint.
         */
        isViewsWithWhen = true;
      } else {
        ({ fieldsWithDeclarativeTargets = [], whens = [] } = getItemsToRecalculate(context));
        isViewsWithWhen = whens.some((val: CalculatedField) => val.type === VIEW);
      }
      const fieldsWithWhen = whens.filter((val: CalculatedField) => val.type === FIELD);
      const changedProperty = Utils.getPropertyName(propertyName);
      const propertyNameArray = changedProperty ? changedProperty.split('.') : [];
      const leafPropertyName = propertyNameArray.pop();
      const parentProperty = propertyNameArray.join('.');
      const serverPrecedenceFields: string[] = [];
      if (parentProperty) {
        updateServerPrecedenceFields(parentProperty, classID, changeSet, leafPropertyName, serverPrecedenceFields);
      }
      payload.serverPrecedenceFields = serverPrecedenceFields;

      let body: RequestBody = {
        ...changeSet.content,
        contextData
      };

      /** merge submitData from parentContext defined on the current context containeritem */
      const parentContextChangeSet = getParentContextChangeSet(context);
      if (!Utils.isEmpty(parentContextChangeSet)) {
        body = mergeObjectsAndConcatArrays(parentContextChangeSet, body);
      }

      const restAPIAndBody = getRestAPIAndBody(
        {
          ...payload,
          fieldsWithDeclarativeTargets,
          fieldsWithWhen,
          isViewsWithWhen
        } as unknown as ActionPayload,
        body
      );
      ActionsSequencer.registerBlockingAction(context).catch(() => {});

      const { restAPI, requestBody, requestType } = restAPIAndBody;
      payload = {
        ...payload,
        requestType
      };
      payload.requestType = requestType;
      const { reqType, restApi } = restAPI;
      const reqHeaders: { [key: string]: string } = { ...getRequestHeaders(isObjectType, state$, context) };
      if (reqHeaders && !reqHeaders.etag && payload.isMultiRecordData && payload.editType === constants.ACTION) {
        reqHeaders.etag = Utils.getETag(payload.context);
      }
      const reqConfig = {
        reqHeaders,
        context,
        method: reqType,
        body: requestBody,
        url: appendQueryParamsForRefreshAPI(payload, restApi)
      };
      return from(refreshCaseViewDataOrAssignment(reqConfig, payload)).pipe(
        map((response) => {
          const responseJSON = triggerTransformCaseToObject(
            response?.data,
            options?.classID as string,
            isObjectType as boolean
          );
          if (options?.resetForm) {
            ContextTreeManager.removeContextTreeNode(context);
          }
          return {
            response: { ...responseJSON },
            actionConfig: payload,
            actionMgrID: payload.options.actionMgrID,
            headers: processHeaders(response.headers)
          };
        }),
        logAndCatchError(
          ': Error occurred during fetch call at refreshData Epic : ',
          payload.context,
          payload?.options?.actionMgrID
        )
      );
    }), // Fetch call to REFRESH_DATA and then pass on the response
    mergeMap(async (data) => {
      if (data && data.type === actionType.ERROR) {
        ActionsSequencer.deRegisterBlockingAction(data.payload?.context).catch(() => {});
        // ERR_CANCELED means the request was superseded by a newer refresh (internal deduplication).
        // Do not publish ON_REFRESH_ERROR or show an error banner — just balance the blocking counter.
        if (data.payload?.err?.code === 'ERR_CANCELED') {
          return [];
        }
        PubSubUtils.publish(constants.ON_REFRESH_ERROR, {
          context: data.payload?.context
        });
        return processDataToDispatchErrorAction(data); // If there is any error emit the ERROR action
      }
      const { actionConfig, response, actionMgrID } = data as Payload;
      const { context, serverPrecedenceFields, requestType } = actionConfig as ActionConfig;
      const vcTargetName = TargetMap.getTargetViewContainerName(actionType.ACTION_SHOWVIEW);
      const target = getResolvedTargetName(vcTargetName, context);
      const options = { acName: target, context };
      const translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
        [publicConstants.STOPMERGEACTIONS]: true,
        [publicConstants.ACTIONS.DELETEAVIALACTIONS]: true
      });
      translatedDXAPIResponse.directUrlUpdation = false;
      if (requestType === refreshConstants.REFRESH) {
        await processViewResponse(translatedDXAPIResponse, actionConfig, options);
      }
      const commonInfo = { target, context };
      const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
      if (actionConfig.interestPage) {
        payload.interestPath = `caseInfo.content${Utils.convertClipboardPageToPageRefNotation(actionConfig.interestPage)}`;
      }
      payload.isArrayDeepMerge = actionConfig.options?.isArrayDeepMerge;
      payload.preserveClientChanges =
        actionConfig.options?.preserveClientChanges !== undefined ? actionConfig.options.preserveClientChanges : true;
      const propertiesToReArrange = CaseUtils.getPropertiesToReArrange(context);
      const refreshPayload = actions.postProcess(actions.mergeData, payload, {
        serverPrecedenceFields,
        propertiesToReArrange
      });
      const pageInstructions = refreshPayload.payload?.data?.pageInstructions;
      if (pageInstructions?.length > 0) {
        delete refreshPayload.payload?.data?.pageInstructions;
        StateMachineManager.mergeServerPageInstructions(context, pageInstructions);
      }
      StateManager.dispatch(refreshPayload);
      ActionManagerInstance.resolveActionByID(actionMgrID, payload);
      PubSubUtils.publish(constants.ON_REFRESH_COMPLETE, {
        context
      });
      // Called last — deferred actions (e.g. FinishAssignment) must only be released after the store is fully updated.
      ActionsSequencer.deRegisterBlockingAction(context).catch(() => {});
      return [];
    })
  );
};

const refreshEpics = [refreshData];
export default refreshEpics;
